import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Bell, Menu, X, LogOut, Leaf } from 'lucide-react';
import AgenticAIChatbot from './AgenticAIChatbot';

const DashboardLayout = ({ children, navItems, role }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Role-based colour tokens (only Tailwind safe-listed classes)
  const roleTheme = {
    patient: { grad: 'from-emerald-600 to-teal-700',   badge: 'bg-emerald-100 text-emerald-700', active: 'bg-emerald-600' },
    doctor:  { grad: 'from-blue-600    to-indigo-700',  badge: 'bg-blue-100    text-blue-700',    active: 'bg-blue-600'    },
    admin:   { grad: 'from-violet-600  to-purple-700',  badge: 'bg-violet-100  text-violet-700',  active: 'bg-violet-600'  },
  };
  const theme = roleTheme[role] || roleTheme.patient;

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'AC';

  const notifPath = '/' + role + '/notifications';
  const profilePath = '/' + role + '/profile';

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      <aside
        className={
          'fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white shadow-xl flex flex-col ' +
          'transform transition-transform duration-300 ' +
          (sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0')
        }
      >
        {/* Logo strip */}
        <div className={'bg-gradient-to-r ' + theme.grad + ' p-5 flex items-center gap-3'}>
          <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-bold text-lg leading-tight">Avartana</h1>
            <p className="text-white/70 text-xs">Care Platform</p>
          </div>
          <button
            className="lg:hidden text-white p-1"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User mini-card -> Links to Profile */}
        <NavLink
          to={profilePath}
          onClick={() => setSidebarOpen(false)}
          title="View Profile"
          className="p-4 border-b bg-gray-50 flex items-center gap-3 hover:bg-gray-100 transition-colors cursor-pointer group"
        >
          <div
            className={'w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 bg-gradient-to-br transition-transform group-hover:scale-105 ' + theme.grad}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm truncate group-hover:text-emerald-700 transition-colors">{user?.name}</p>
            <span className={'text-xs px-2 py-0.5 rounded-full font-medium capitalize ' + theme.badge}>
              {role}
            </span>
          </div>
        </NavLink>

        {/* Nav links */}
        <nav className="flex-1 p-3 overflow-y-auto space-y-0.5">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ' +
                (isActive
                  ? theme.active + ' text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={'w-4 h-4 flex-shrink-0 ' + (isActive ? 'text-white' : 'text-gray-500')}
                  />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sign out */}
        <div className="p-3 border-t">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main area ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top bar */}
        <header className="bg-white border-b px-4 lg:px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <p className="text-xs text-gray-400 hidden sm:block">
              {new Date().toLocaleDateString('en-IN', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
              })}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <NavLink
              to={notifPath}
              className="relative p-2 rounded-lg hover:bg-gray-100"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </NavLink>
            <NavLink
              to={profilePath}
              title="View Profile"
              className="group relative transition-transform hover:scale-105 active:scale-95"
            >
              <div
                className={'w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs bg-gradient-to-br ring-2 ring-transparent group-hover:ring-emerald-400 transition-all ' + theme.grad}
              >
                {initials}
              </div>
            </NavLink>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Floating Agentic AI Assistant Chatbot */}
      <AgenticAIChatbot />
    </div>
  );
};

export default DashboardLayout;
